import { useEffect, useState } from 'react';

interface GHLContext {
  locationId: string;
  userId: string;
  companyId: string;
  userEmail?: string;
  userName?: string;
  accessToken?: string;
  ssoToken?: string;
}

export function useGHLIframe() {
  const [context, setContext] = useState<GHLContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState<string | null>(null);

  // Request encrypted SSO data from GHL parent window
  const requestSSOData = async (): Promise<string | null> => {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.log('[useGHLIframe] SSO request timed out after 3 seconds');
        resolve(null);
      }, 3000);

      const messageHandler = (event: MessageEvent) => {
        // Verify origin
        const isGHL = event.origin.includes('gohighlevel.com') || 
                      event.origin.includes('msgsndr.com') ||
                      event.origin.includes('oasisautokonnect.com');
        
        if (!isGHL) {
          return;
        }

        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          
          if (data.message === 'REQUEST_USER_DATA_RESPONSE') {
            clearTimeout(timeout);
            window.removeEventListener('message', messageHandler);
            resolve(data.payload);
          }
        } catch (err) {
          console.error('[useGHLIframe] Error parsing message:', err);
        }
      };

      window.addEventListener('message', messageHandler);
      
      // Request user data from parent
      if (window.parent !== window) {
        window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
      } else {
        clearTimeout(timeout);
        resolve(null);
      }
    });
  };


  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let retryInterval: NodeJS.Timeout;
    let lastLocationId: string | null = null;
    
    // Check if we're in an iframe
    const isInIframe = window.self !== window.top;
    
    // Request SSO data and decrypt it
    const initializeContext = async () => {
      // Only try SSO if we're in an iframe
      if (!isInIframe) {
        // Set loading to false so OAuth can take over
        setIsLoading(false);
        return;
      }
      
      const encryptedData = await requestSSOData();
      
      if (encryptedData) {
        
        try {
          // Send encrypted data to backend for decryption
          const response = await fetch(`${import.meta.env.VITE_API_URL}/decrypt-sso`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ encryptedData })
          });
          
          const userData = await response.json() as {
            success: boolean;
            locationId: string;
            userId: string;
            companyId: string;
            email?: string;
            name?: string;
          };
          
          if (userData.success) {
            setContext({
              locationId: userData.locationId,
              userId: userData.userId,
              companyId: userData.companyId,
              userEmail: userData.email,
              userName: userData.name,
              accessToken: undefined,
              ssoToken: encryptedData,
            });

            lastLocationId = userData.locationId;

            // Phase A.2: exchange the same SSO blob for an httpOnly user-session
            // cookie. Fire-and-forget — nothing in the app reads the cookie yet
            // (Phase A.3 wires shadow-mode middleware that will). If this fails,
            // the app still works exactly as it does today; A.3 logs will reveal
            // the failure rate before any enforcement turns on.
            fetch(`${import.meta.env.VITE_API_URL}/auth/exchange-sso`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ encryptedData }),
            }).catch(() => {
              // Silent — observed via shadow logs in Phase A.3, not via console.
            });

            setIsLoading(false);

            // Clear the timeout and retry interval since SSO succeeded
            clearTimeout(timeoutId);
            clearInterval(retryInterval);

            return;
          }
        } catch (error) {
          console.error('[useGHLIframe] Failed to decrypt SSO data:', error);
        }
      }
      
      // Fallback to legacy iframe context if SSO fails
      console.log('[useGHLIframe] SSO failed, falling back to legacy context request');
    };
    
    initializeContext();

    // Legacy fallback: Handle old-style GHL context messages
    const handleMessage = (event: MessageEvent) => {
      const isGHL = event.origin.includes('gohighlevel.com') || 
                    event.origin.includes('msgsndr.com') ||
                    event.origin.includes('oasisautokonnect.com');
      const isLocalhost = event.origin.includes('localhost') || event.origin.includes('127.0.0.1');
      
      if (!isGHL && !isLocalhost) return;

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data.type === 'ghl-context' || data.context) {
          const ghlContext = data.context || data;
          const newLocationId = ghlContext.locationId || ghlContext.location_id;
          
          if (lastLocationId && newLocationId && lastLocationId !== newLocationId) {
            console.log('[useGHLIframe] Location changed:', lastLocationId, '→', newLocationId);
          }
          
          lastLocationId = newLocationId;
          
          setContext({
            locationId: newLocationId,
            userId: ghlContext.userId || ghlContext.user_id,
            companyId: ghlContext.companyId || ghlContext.company_id,
            userEmail: ghlContext.userEmail || ghlContext.user_email,
            userName: ghlContext.userName || ghlContext.user_name,
            accessToken: ghlContext.accessToken || ghlContext.access_token,
            ssoToken: ghlContext.ssoToken || ghlContext.sso_token,
          });
          
          setIsLoading(false);
          clearInterval(retryInterval);
          clearTimeout(timeoutId);
        }
      } catch (err) {
        console.error('[useGHLIframe] Error parsing message:', err);
      }
    };

    // Listen for legacy context messages
    window.addEventListener('message', handleMessage);

    // Legacy fallback: Request old-style context
    const requestContext = () => {
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'request-context' }, '*');
      }
    };

    // Retry legacy context request as fallback
    retryInterval = setInterval(requestContext, 1000);
    
    // Timeout after 5 seconds - fail fast to allow OAuth fallback
    timeoutId = setTimeout(() => {
      clearInterval(retryInterval);
      
      if (!context) {
        console.log('[useGHLIframe] Context loading timed out, will use OAuth fallback');
        setIsLoading(false);
        // Don't set error - let OAuth handle authentication
      }
    }, 5000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(retryInterval);
      clearTimeout(timeoutId);
    };
  }, []);

  return { context, isLoading, error };
}
